export const PLATFORM_FORM_TEXTS = {
  // Steps
  STEPS: {
    STEP_1: 'User permissions',
    STEP_2: 'Offering overview',
    STEP_3: 'Offering details',
    STEP_4: 'Features, capabilities & use cases',
    STEP_5: 'Summary',
  },

  // Page headers
  HEADERS: {
    ADD_NEW: 'Add new offering',
    EDIT: 'Edit',
    OFFERING_TYPE: 'Platform',
  },

  // Step 1: User permissions
  PERMISSIONS: {
    OWNER: {
      SECTION_TITLE: 'Content Owner Information',
      LABEL: "Content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the content owner. The content owner should be the person accountable for this offering, such as a Product Manager or a Product Line Lead.',
    },
    DEPUTY: {
      SECTION_TITLE: 'Deputy Content Owner Information',
      LABEL: "Deputy content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the deputy content owner. The deputy content owner serves as the backup accountable person for this offering. This role can be filled by anyone on the team.',
    },
    AUTHOR: {
      SECTION_TITLE: 'Content Author Information',
      LABEL: "Content author's email address",
      PLACEHOLDER: 'e.g. email.one@roche.com',
      HINT: 'Enter the email address of the content author. The content author is responsible for creating and maintaining content for this offering. Multiple email addresses should be separated by commas.',
    },
  },

  // Step 2: Offering overview
  OVERVIEW: {
    GENERAL_INFO: {
      SECTION_TITLE: 'General Information',
      DISPLAY_NAME: {
        LABEL: 'Offering name',
        PLACEHOLDER: 'e.g. Gmail',
        HINT: 'Enter the offering display name. This is the name that users will see in Panorama.',
      },
      APPROVAL_STATUS: {
        LABEL: 'Approval status',
        PLACEHOLDER: 'Select approval status for your offering',
        HINT: 'Select approval status that is most appropriate for your offering.',
      },
      SUCCESSOR: {
        LABEL: 'Successor (optional)',
        PLACEHOLDER: 'Select successor for your offering',
        HINT: 'Select the approved offering that will be used to replace this offering.',
      },
    },
    DESCRIPTION: {
      SECTION_TITLE: 'Offering Description',
      LABEL: 'Offering description',
      PLACEHOLDER: 'e.g. Simplifying Workflows and Driving Collaboration',
      HINT: 'Enter a high level description of the purpose and value of the offering (25 words or less).',
    },
    OFFERING_RELATIONSHIP: {
      SECTION_TITLE: 'Offering Relationship (Optional)',
      CHECKBOX_LABEL: 'Offering relationship is available',
      RELATIONSHIP_TYPE: {
        LABEL: 'Relationship type',
        PLACEHOLDER: 'Select relationship type',
        HINT: 'Specifies if the current offering is the child or the parent in this specific link.',
      },
      SELECT_PARENT: {
        LABEL: 'Parent platform',
        PLACEHOLDER: 'Search and select parent platform',
        HINT: 'Select the platform that is parent of your offering.',
      },
      SELECT_CHILD: {
        LABEL: 'Child offering',
        PLACEHOLDER: 'Search and select child offering',
        HINT: 'Select the offering that is child of your platform.',
      },
    },
    SIMILAR_OFFERINGS: {
      SECTION_TITLE: 'Similar Offerings In Panorama (optional)',
      LABEL: 'Similar offerings in Panorama (optional)',
      PLACEHOLDER: 'Select similar offerings for your offering',
      HINT: 'Select other offerings with similar capabilities. Similar offerings will be associated with your offering. Only offerings approved in Panorama can be selected.',
    },
  },

  // Step 3: Offering details
  DETAILS: {
    ONBOARDING: {
      SECTION_TITLE: 'Onboarding information',
      LABEL: 'Steps to access',
      HINT: 'Add a sequential list of actions required to install or initialize the offering',
      ACCESS_INSTRUCTIONS: {
        LABEL: 'Access instructions',
        PLACEHOLDER: 'Enter access instructions...',
      },
      TABLE_COLUMNS: {
        STEP_DESCRIPTION: 'Step description',
        ACCESS_URL: 'Access URL',
      },
      CTA: 'Add new Step to access',
    },
    QUALIFICATION: {
      SECTION_TITLE: 'Qualification status',
      STATUS: {
        LABEL: 'Qualification status',
        PLACEHOLDER: 'Select qualification status',
        HINT: 'Select the status that indicates whether the offering is formally qualified or not.',
      },
      DOCUMENTATION: {
        LABEL: 'Qualification documentation',
        HINT: 'Add a direct link to the Veeva Vault folder containing the Operational Support Plan (OSP), Service Level Agreement (SLA), Operational-Level Agreement (OLA), and other validation docs.',
        TABLE_COLUMNS: {
          NAME: 'Qualification document name',
          URL: 'Qualification document URL',
        },
        CTA: 'Add new Qualification Documentation',
      },
    },
    AVAILABLE_VERSION: {
      SECTION_TITLE: 'Available Versions (optional)',
      HINT: 'Add a list of the latest versions of the offerings for users to use.',
      TABLE_COLUMNS: {
        VERSION: 'Version',
        STATUS: 'Status',
        COMPATIBILITY: 'Compatibility',
        RELEASE_DATE: 'Release date',
        END_OF_LIFE_DATE: 'End-of-life date',
        ADDITIONAL_INFORMATION: 'Additional information',
      },
      CTA: 'Add new Available Version',
    },
    SUPPORT: {
      SECTION_TITLE: 'Support',
      AVAILABILITY: {
        LABEL: 'Support availability',
        PLACEHOLDER: 'Select the support availability status',
        HINT: 'Indicate whether your offering includes dedicated support coverage at Roche.',
      },
      CHANNEL: {
        LABEL: 'Support channels',
        HINT: 'Provide information about dedicated support coverage. This will serve as the primary guideline for your users on how to obtain support.',
        TABLE_COLUMNS: {
          CHANNEL_TYPE: 'Channel',
          DESTINATION: 'Destination',
          DESCRIPTION: 'Channel description',
          SERVICE_DESK_NAME: 'Support instance name',
        },
        CTA: 'Add new Support channel',
      },
      HOUR: {
        LABEL: 'Support hours',
        HINT: 'Configure when the support team is active and how fast they respond.',
        TABLE_COLUMNS: {
          DAYS: 'Support days',
          START_TIME: 'Operating hours start at',
          END_TIME: 'Operating hours end at',
          TIMEZONE: 'Timezone',
          RESPONSE_TIME: 'Expected response time',
        },
        CTA: 'Add new Support hours',
      },
    },

    ENVIRONMENT: {
      SECTION_TITLE: 'Environment & Service Level Agreement (optional)',
      HINT: 'Add the specific deployment environments where this offering is active, paired with their respective service level commitments.',
      TABLE_COLUMNS: {
        NAME: 'Environment',
        URL: 'Environment URL',
        SLA: 'Service Level Agreement (SLA)',
      },
      CTA: 'Add new Environment & Service Level Agreement',
    },
    SRA: {
      SECTION_TITLE: 'System Risk Assessment',
      ASSESSMENT_ID: {
        LABEL: 'System Risk Assessment ID',
        PLACEHOLDER: 'e.g. RI0000000',
        HINT: 'Enter the System Risk Assessment ID for this offering from irm.roche.com. Use the applicable format for System Risk Assessment.',
      },
      DATA_CONFIDENTIALITY: {
        LABEL: 'Data confidentiality level',
        PLACEHOLDER: 'Select data confidentiality level',
        HINT: 'Select the highest confidentiality level of data that your offering is approved to store (C1, C2, C3, or C4).',
      },
      RETENTION_PERIOD: {
        LABEL: 'Retention period (optional)',
        PLACEHOLDER: 'e.g. 3 years, 7 years, 10 years, or none',
        HINT: 'Describe how long the data can be retained (e.g., 3 years, 7 years, 10 years, or none).',
      },
      GXP_COMPLIANCE: {
        LABEL: 'GxP Compliance',
        PLACEHOLDER: 'Select GxP compliance status',
        HINT: 'Select the status that indicates whether the offering is GxP compliant.',
      },
      REQUIRED_FOR_SOLUTIONS: {
        LABEL:
          'System Risk Assessment is required for solutions built on this platform',
      },
    },
    SUPPORTED_DATA_CLASSIFICATION: {
      SECTION_TITLE: 'Supported Data Classification (optional)',
      CLASSIFICATION_LEVEL: {
        LABEL: 'Supported data classification level',
        PLACEHOLDER: 'Select supported data classification level',
      },
      ADDITIONAL_INFORMATION: {
        LABEL: 'Additional information',
        PLACEHOLDER:
          'Enter additional information about supported data classification...',
      },
    },
    ADDITIONAL_COST: {
      SECTION_TITLE: 'Additional Cost Considerations (optional)',
      ADDITIONAL_COST_CONSIDERATIONS: {
        CHECKBOX_LABEL: 'Additional cost considerations available',
        LABEL: 'Additional cost considerations',
        PLACEHOLDER: 'Enter additional cost considerations',
        HINT: 'Enter details for additional costs (e.g. development costs).',
      },
      OTHER_PRICING_MODELS: {
        CHECKBOX_LABEL: 'Other pricing models available',
        LABEL: 'Other pricing models',
        PLACEHOLDER: 'Enter other available pricing models details',
        HINT: 'Enter other available pricing models (e.g., Cost per 10 users).',
      },
    },
    LICENSE: {
      SECTION_TITLE: 'License Cost Responsibility',
      LICENSE_COST_RESPONSIBILITY: {
        LABEL: 'License cost responsibility',
        PLACEHOLDER: 'Select the license cost responsibility',
        HINT: 'Indicate who pays for the license.',
      },
      PRICING_PER_USER: {
        LABEL: 'Pricing per user available',
        HINT: 'Define if per-user pricing applies. This determines the pricing cost for a single user.',
      },
      VALUE: {
        LABEL: 'Value',
        PLACEHOLDER: 'e.g. 10',
        HINT: 'Enter cost value (number only).',
      },
      CURRENCY: {
        LABEL: 'Currency',
        PLACEHOLDER: 'Select',
        HINT: 'Select currency for the licensing cost (CHF, USD, EUR).',
      },
      PAYMENT_FREQUENCY: {
        LABEL: 'Frequency',
        PLACEHOLDER: 'Select',
        HINT: 'Select the payment frequency (monthly, weekly, annually, or one-time).',
      },
    },
    DEVELOPMENT_COST: {
      SECTION_TITLE: 'Development Cost Responsibility',
      RESPONSIBILITY: {
        LABEL: 'Development cost responsibility',
        PLACEHOLDER:
          'Select the infrastructure development cost responsibility',
        HINT: 'Indicate which party is financially responsible for the initial development costs of applications or solutions built using this offering.',
      },
      OTHER_DETAILS: {
        LABEL: 'Other development cost details (optional)',
        HINT: 'Enter specific context or a description of the development cost arrangement.',
      },
    },
    SUPPORT_MAINTENANCE_COST: {
      SECTION_TITLE: 'Support & Maintenance Cost Responsibility',
      RESPONSIBILITY: {
        LABEL: 'Support maintenance cost responsibility',
        PLACEHOLDER:
          'Select infrastructure support maintenance cost responsibility',
        HINT: 'Indicate who bears the ongoing costs for bug fixes, updates, and maintenance of solutions post-deployment.',
      },
      OTHER_DETAILS: {
        LABEL: 'Other support & maintenance cost details (optional)',
        HINT: 'Enter specific context for the maintenance cost structure.',
      },
    },
    HOSTING: {
      SECTION_TITLE: 'Hosting Information (optional)',
      HINT: 'Add details regarding the underlying infrastructure and hosting provider for the offering.',
      TABLE_COLUMNS: {
        NAME: 'Hosting provider name',
        TYPE: 'Hosting type',
        MANAGED: 'Hosting management',
      },
      CTA: 'Add new Hosting Information',
    },
    SSO: {
      SECTION_TITLE: 'Single Sign On (optional)',
      SUPPORT: {
        LABEL: 'Single Sign On Support',
        CHECKBOX_LABEL: 'Single Sign On Supported',
        HINT: 'Indicate whether Single Sign-On is supported for your offering.',
      },
      PROVIDER: {
        LABEL: 'Single Sign On provider',
        PLACEHOLDER: 'e.g. Google',
        HINT: 'Enter supported Single Sign On provider name.',
      },
    },
    AI_AGENT: {
      SECTION_TITLE: 'AI Agent (optional)',
      HINT: 'Add a list of internal AI agents, such as Lumin, associated with this offering.',
      TABLE_COLUMNS: {
        NAME: 'AI Agent name',
        URL: 'AI Agent URL',
      },
      CTA: 'Add new AI Agent',
    },
    COMMUNITY: {
      SECTION_TITLE: 'Community (optional)',
      HINT: 'Add information about the user communities or collaborative spaces associated with the offering.',
      TABLE_COLUMNS: {
        TITLE: 'Community name',
        DESCRIPTION: 'Community description',
        URL: 'Community URL',
      },
      CTA: 'Add new Community',
    },
    SERVICE_AND_REQUEST: {
      SECTION_TITLE: 'Services & Requests',
      HINT: 'Add request channels available for this offering, such as consultancy, support, incident, or general requests.',
      TABLE_COLUMNS: {
        ID: 'Request ID',
        TYPE: 'Request type',
        TITLE: 'Request title',
        DESCRIPTION: 'Instructions for users',
        DESTINATION: 'Destination URL',
      },
      CTA: 'Add new Service & Request',
    },
    VENDOR: {
      SECTION_TITLE: 'Vendor information',
      NAME: {
        LABEL: 'Vendor name',
        PLACEHOLDER: 'e.g. Salesforce',
        HINT: 'Enter the name of the vendor or supplier.',
      },
      SUPPORT: {
        LABEL: 'Supported services (optional)',
        PLACEHOLDER:
          'Enter the details of the support services contracted with the vendor',
        HINT: 'Add a list of the support services contracted with vendor.',
      },
      URL: {
        LABEL: 'Vendor URL (optional)',
        HINT: "Add a direct link to the vendor's primary website or support information.",
      },
    },
    ADDONS: {
      SECTION_TITLE: 'Available Add-ons (optional)',
      CHECKBOX_LABEL: 'Add-ons available',
      CHECKBOX_HINT:
        'Indicate whether approved add-ons are available for your offering.',
      TABLE_LABEL: 'Add-ons details',
      TABLE_HINT:
        'Add a list of approved plugins, integrations, or extensions available for your offering.',
      TABLE_COLUMNS: {
        NAME: 'Add-on name',
        DESCRIPTION: 'Description',
        COST: 'Cost information',
        SUPPORT: 'Support availability',
        URL: 'Add-on URL',
      },
      CTA: 'Add new Add-on',
    },
    EXTERNAL_USE: {
      SECTION_TITLE: 'External use',
      LABEL: 'Allowance for external use',
      PLACEHOLDER: 'Select the option for external use allowance',
      HINT: 'Indicate whether solutions built or hosted on this platform are allowed for use by external third parties or outside the primary Roche/Genentech organization.',
    },
  },

  // Step 4: Features and use cases
  FUNCTIONALITY: {
    CAPABILITIES: {
      SECTION_TITLE: 'Major Capabilities',
      HINT: 'Add core capabilities that describe broad abilities associated with the offering.',
      TABLE_COLUMNS: {
        CAPABILITY_NAME: 'Capability name',
        CAPABILITY_DESCRIPTION: 'Capability description',
        CAPABILITY_BENEFIT: 'Capability benefit',
      },
      CTA: 'Add new Major Capability',
    },
    FEATURES: {
      SECTION_TITLE: 'Features',
      HINT: 'Add a list of the major functionalities or capabilities of your offering. Include the feature name, a brief explanation of its purpose, and the advantages or value it provides.',
      TABLE_COLUMNS: {
        FEATURE_NAME: 'Feature name',
        FEATURE_DESCRIPTION: 'Feature description',
        FEATURE_BENEFIT: 'Feature benefit',
      },
      CTA: 'Add new feature',
    },
    USE_CASES: {
      SECTION_TITLE: 'Use cases (optional)',
      HINT: 'Add a set of typical scenarios or contexts in which the offering is used. Include the use case name, a brief description of how the offering is utilized, and by whom.',
      TABLE_COLUMNS: {
        USE_CASE_NAME: 'Use case name',
        USE_CASE_DESCRIPTION: 'Use case description',
      },
      CTA: 'Add new use case',
    },
  },
};

// Export individual sections for easier imports if needed
export const STEP_LABELS = PLATFORM_FORM_TEXTS.STEPS;
